import { DailyPnlItem } from '@/apis/idea/idea-api.def.ts'
import { IdeyaAgGridTable } from '@/common/ag-grid/ideya-ag-grid-table.tsx'
import { AutoSizeStrategy, ColDef } from 'ag-grid-community'
import { useMemo, useRef } from 'react'
import { IdeaHistoryListItem } from './pnl-history-table.def.ts'
import { AgGridReact } from 'ag-grid-react'
import { IDEA_HISTORY_COLUMN_STATE_KEY } from '@/utils/constants.ts'
import {
  currencyValueFormatter,
  marketDateTimezoneFormatter,
} from '@/utils/formatters.ts'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useAppSelector } from '@/redux/hooks.ts'
import { selectThemeStyle } from '@/features/setting/setting.slice.ts'
import { RowClassParams } from 'ag-grid-enterprise'

interface PnlHistoryTableProps {
  bookActiveDate: string
  dailyPnlData: DailyPnlItem[]
  currency: string
}

export const PnlHistoryTable: React.FC<PnlHistoryTableProps> = ({
  bookActiveDate,
  dailyPnlData,
  currency,
}) => {
  const gridRef = useRef<AgGridReact>(null)
  const themeStyle = useAppSelector(selectThemeStyle)
  const { t } = useTranslation()

  // Calculating row data
  const rowData: IdeaHistoryListItem[] = useMemo(() => {
    return dailyPnlData.map((item) => {
      return {
        date: item.positionDate,
        shares: item.shares,
        avgCost: item.avgCost,
        eodPrice: item.eodPrice,
        fxRate: item.fxRate,
        pnl: item.pnl,
        realizedPnl: item.realizedPnl,
        unrealizedPnl: item.unrealizedPnl,
        overallCost: item.overallCost,
      }
    })
  }, [dailyPnlData])

  const columns: ColDef[] = useMemo(
    () => [
      {
        headerName: t('idea_list.idea_modal.pnl_history_table.columns.date'),
        field: 'date',
        valueFormatter: (params) =>
          marketDateTimezoneFormatter(params, {
            showTime: false,
            showTimezone: false,
          }),
      },
      {
        headerName: t('idea_list.idea_modal.pnl_history_table.columns.shares'),
        field: 'shares',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, { decimal: 0 }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.pnl_history_table.columns.avg_cost'
        ),
        type: 'numberColumn',
        field: 'avgCost',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: currency,
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.pnl_history_table.columns.eod_price'
        ),
        field: 'eodPrice',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: currency,
          }),
      },
      {
        headerName: t('idea_list.idea_modal.pnl_history_table.columns.fx_rate'),
        field: 'fxRate',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
          }),
      },
      {
        headerName: t('idea_list.idea_modal.pnl_history_table.columns.pnl'),
        field: 'pnl',
        type: 'numberColumn',
        aggFunc: 'sum',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 0,
            currency: 'USD',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.pnl_history_table.columns.realized_pnl'
        ),
        field: 'realizedPnl',
        type: 'numberColumn',
        aggFunc: 'sum',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 0,
            currency: 'USD',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.pnl_history_table.columns.unrealized_pnl'
        ),
        field: 'unrealizedPnl',
        type: 'numberColumn',
        aggFunc: 'sum',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 0,
            currency: 'USD',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.pnl_history_table.columns.overall_cost'
        ),
        field: 'overallCost',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: 'USD',
          }),
      },
    ],
    [t]
  )

  const handleGridReady = () => {
    if (!gridRef.current) return
    const savedState = localStorage.getItem(IDEA_HISTORY_COLUMN_STATE_KEY)
    if (savedState) {
      const state = JSON.parse(savedState)
      gridRef.current.api.applyColumnState({
        state: state,
        applyOrder: true,
      })
    }
    gridRef.current.api.sizeColumnsToFit()
  }

  const handleColumnChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(IDEA_HISTORY_COLUMN_STATE_KEY, JSON.stringify(state))
  }

  const handleSortChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(IDEA_HISTORY_COLUMN_STATE_KEY, JSON.stringify(state))
  }

  const getRowStyle = (params: RowClassParams) => {
    if (
      params.data &&
      dayjs(params.data.date).isBefore(dayjs(bookActiveDate))
    ) {
      const color = themeStyle.disabledBookName
      if (color) {
        return { color }
      }
    }
    return undefined
  }

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    suppressHeaderMenuButton: true,
  }

  const autoSizeStrategy = {
    type: 'fitCellContents',
  } as AutoSizeStrategy

  return (
    <IdeyaAgGridTable
      ref={gridRef}
      columns={columns}
      defaultColDef={defaultColDef}
      rowData={rowData}
      getRowStyle={getRowStyle}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onSortChanged={handleSortChanged}
      autoSizeStrategy={autoSizeStrategy}
      height={400}
    />
  )
}
